import { LocationType, useFrontendSubscriptionStatus } from '@cmctechnology/webinvest-store-frontend';
import isEqual from 'lodash.isequal';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ACCOUNT_API_NAMES } from './apis';
import { subscribeConfirmations, unsubscribeConfirmations } from './confirmationActions';
import { makeConfirmationsKey, makeConfirmationsParamKey, makeConfirmationsParamSubscriptionKey } from './keys';
import { ACCOUNT_STORE_NAME, IStore } from './store';
import { AccountKey, ConfirmationsParamKey, Sort } from './types';

export const useSubscribeConfirmations = (
  location: LocationType,
  accountNo: AccountKey,
  sortColumn: string,
  sortDirection: Sort,
  from: Date,
  to: Date,
  pageSize: number,
  page: number
) => {
  const dispatch = useDispatch();
  const paramsKey = makeConfirmationsParamKey(pageSize, sortColumn, sortDirection);
  const confirmations = useSelector(
    (store: IStore) => store[ACCOUNT_STORE_NAME].confirmations[accountNo]?.from[from.valueOf()]?.to[to.valueOf()]?.params[paramsKey]?.pages[page]?.items
  );
  const pageCount = useSelector((store: IStore) => store[ACCOUNT_STORE_NAME].confirmations[accountNo]?.from[from.valueOf()]?.to[to.valueOf()]?.count);
  const countApiStatus = useFrontendSubscriptionStatus(ACCOUNT_STORE_NAME, ACCOUNT_API_NAMES.ConfirmationsCount1, makeConfirmationsKey(accountNo, from, to));
  const pageApiStatus = useFrontendSubscriptionStatus(
    ACCOUNT_STORE_NAME,
    ACCOUNT_API_NAMES.ConfirmationsGet3,
    makeConfirmationsParamSubscriptionKey(accountNo, from, to, pageSize, sortColumn, sortDirection, page)
  );
  const [prevArgs, setPrevArgs] = useState<ConfirmationsParamKey>();
  const args: ConfirmationsParamKey = { accountNo, sortColumn, sortDirection, from: from.valueOf(), to: to.valueOf(), pageSize, page };

  useEffect(() => {
    dispatch<any>(subscribeConfirmations(location, accountNo, sortColumn, sortDirection, from, to, pageSize, page));
    setPrevArgs(args);
    return () => {
      dispatch<any>(unsubscribeConfirmations(location, accountNo, sortColumn, sortDirection, from, to, pageSize, page));
    };
  }, []);

  useEffect(() => {
    if (prevArgs == undefined || isEqual(args, prevArgs)) return;

    dispatch<any>(
      unsubscribeConfirmations(
        location,
        prevArgs.accountNo,
        prevArgs.sortColumn,
        prevArgs.sortDirection,
        new Date(prevArgs.from),
        new Date(prevArgs.to),
        prevArgs.pageSize,
        prevArgs.page
      )
    );
    dispatch<any>(subscribeConfirmations(location, accountNo, sortColumn, sortDirection, from, to, pageSize, page));
    setPrevArgs(args);
    return () => {
      dispatch<any>(unsubscribeConfirmations(location, accountNo, sortColumn, sortDirection, from, to, pageSize, page));
    };
  }, [accountNo, from, to, pageSize, page]);

  return {
    loading: countApiStatus.fetching || pageApiStatus.fetching,
    polling: pageApiStatus.polling,
    error: countApiStatus.error ?? pageApiStatus.error,
    data: { confirmations, pageCount },
    fetching: countApiStatus.fetching || pageApiStatus.fetching
  };
};
